import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { IDS } from '@/constants/testIds';

const HERO_IMG = 'https://images.pexels.com/photos/16497359/pexels-photo-16497359.jpeg?auto=compress&w=1800&q=85';
const SECONDARY = 'https://customer-assets-lxgj4vgw.emergentagent.net/job_holi-diwali-india/artifacts/sdztug2m_IMG_20241113_100008506.webp';

const line = {
  hidden: { y: '110%' },
  visible: (i = 0) => ({
    y: '0%',
    transition: { delay: 0.35 + i * 0.14, duration: 1.15, ease: [0.19, 1, 0.22, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 800], [0, 220]);
  const y2 = useTransform(scrollY, [0, 800], [0, -120]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.12]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.4]);

  return (
    <section ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-background" data-testid="hero-section">
      {/* Background image with parallax */}
      <motion.div style={{ y: y1, scale }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Тадж-Махал на закате" className="w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/60 to-white/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-white/30 to-white/60" />
      </motion.div>

      {/* Meta strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute top-24 md:top-28 left-0 right-0 z-10"
      >
        <div className="container-x flex items-center justify-between text-xs uppercase tracking-widest2 text-foreground/70 font-medium">
          <span>№ 01 · Manifesto</span>
          <span className="hidden md:block">28° 36′ 50″ N · 77° 12′ 32″ E</span>
          <span>Season 2026</span>
        </div>
      </motion.div>

      {/* Central copy */}
      <div className="container-x relative z-10 min-h-[100svh] flex flex-col justify-center pt-32 pb-24">
        <div className="max-w-[1100px]">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="overline mb-8"
          >
            С русскоговорящим гидом Али
          </motion.p>

          <h1 data-testid={IDS.heroTitle} className="font-serif-display text-[15vw] md:text-[10.5rem] lg:text-[13rem] leading-[0.88] text-foreground">
            <span className="block reveal-mask">
              <motion.span variants={line} initial="hidden" animate="visible" custom={0} className="reveal-inner">
                Откройте
              </motion.span>
            </span>
            <span className="block reveal-mask italic text-saffron">
              <motion.span variants={line} initial="hidden" animate="visible" custom={1} className="reveal-inner">
                настоящую
              </motion.span>
            </span>
            <span className="block reveal-mask">
              <motion.span variants={line} initial="hidden" animate="visible" custom={2} className="reveal-inner">
                Индию.
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.8 }}
            className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
          >
            <p className="max-w-md text-foreground/90 text-base md:text-lg leading-relaxed font-light">
              Групповые многодневные путешествия по Северной Индии — Дивали, Холи, Новый год, Восьмое марта Девичник, Золотой треугольник и Ганг.
              <br />
              <span className="text-foreground/70 italic">Не турагентство. Автор — Али.</span>
            </p>

            <a
              data-testid={IDS.heroCta}
              href="#festival"
              onClick={(e) => { e.preventDefault(); document.getElementById('festival')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group inline-flex items-center gap-4 text-xs uppercase tracking-widest2 font-bold text-saffron self-start md:self-end"
            >
              <span className="w-16 h-px bg-saffron transition-all group-hover:w-24" />
              Посмотреть туры
            </a>
          </motion.div>
        </div>
      </div>

      {/* Secondary floating image (parallax counterpoint) */}
      <motion.div
        style={{ y: y2, opacity }}
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.5, duration: 1.2, ease: [0.19, 1, 0.22, 1] }}
        className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 w-[280px] h-[380px] overflow-hidden z-10"
      >
        <img src={SECONDARY} alt="Али — русскоговорящий гид" className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 border border-foreground/20" />
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        data-testid={IDS.heroScroll}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.6 }}
        className="absolute bottom-8 left-0 right-0 z-10 pointer-events-none"
      >
        <div className="container-x flex justify-between items-center text-xs uppercase tracking-widest2 text-foreground/60">
          <span>Scroll ↓</span>
          <span className="italic font-serif-display text-base text-foreground/70">— «Индия — не страна, а состояние»</span>
        </div>
      </motion.div>
    </section>
  );
}
